import { useCallback, useRef, useState, useEffect } from "react";
import { Download, ChevronDown, FileText } from "lucide-react";
import * as htmlToImage from "html-to-image";
import { useTranslation } from "react-i18next";
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";

interface ExportMenuProps {
  contentRef: React.RefObject<HTMLDivElement>;
  fileName: string;
}

export function ExportMenu({ contentRef, fileName }: ExportMenuProps) {
  const { t } = useTranslation();
  const [isDownloading, setIsDownloading] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [backgroundColor, setBackgroundColor] = useState("#eeeeee");
  const dropdownRef = useRef<HTMLDivElement>(null);

  const applyStylesToElements = () => {
    // Handle SVG elements specifically
    const svgElements = document.querySelectorAll("svg");
    svgElements.forEach((svg) => {
      // Ensure SVGs have explicitly set dimensions
      if (svg.classList.contains("overflow-visible")) {
        svg.setAttribute("overflow", "visible");
      }

      // Apply computed styles to text elements within SVG
      const textElements = svg.querySelectorAll("text");
      textElements.forEach((text) => {
        const computedStyle = window.getComputedStyle(text);
        text.setAttribute("font-size", computedStyle.fontSize);
        text.setAttribute("font-weight", computedStyle.fontWeight);
        text.setAttribute("fill", computedStyle.fill || computedStyle.color);
      });
    });
  };

  const handleDownloadPNGTransparent = useCallback(async () => {
    setIsDropdownOpen(false);
    applyStylesToElements();
    await new Promise((resolve) => setTimeout(resolve, 100));
    if (contentRef.current) {
      try {
        setIsDownloading(true);

        const dataUrl = await htmlToImage.toPng(contentRef.current, {
          backgroundColor: "transparent",
          quality: 1.0,
          pixelRatio: 2,
        });

        // Create temporary link and trigger download
        const link = document.createElement("a");
        link.href = dataUrl;
        link.download = `${fileName}-content.png`;
        link.click();
      } catch (error) {
        console.error("Error generating transparent PNG image:", error);
      } finally {
        setIsDownloading(false);
      }
    }
  }, [fileName, contentRef]);

  const handleDownloadPNGWithBackground = useCallback(async () => {
    setIsDropdownOpen(false);
    applyStylesToElements();
    await new Promise((resolve) => setTimeout(resolve, 100));
    if (contentRef.current) {
      try {
        setIsDownloading(true);

        const dataUrl = await htmlToImage.toPng(contentRef.current, {
          backgroundColor: backgroundColor,
          quality: 1.0,
          pixelRatio: 2,
        });

        // Create temporary link and trigger download
        const link = document.createElement("a");
        link.href = dataUrl;
        link.download = `${fileName}-content.png`;
        link.click();
      } catch (error) {
        console.error("Error generating PNG with background image:", error);
      } finally {
        setIsDownloading(false);
      }
    }
  }, [backgroundColor, fileName, contentRef]);

  const handleDownloadJPEG = useCallback(async () => {
    setIsDropdownOpen(false);
    applyStylesToElements();
    await new Promise((resolve) => setTimeout(resolve, 100));
    if (contentRef.current) {
      try {
        setIsDownloading(true);

        const dataUrl = await htmlToImage.toJpeg(contentRef.current, {
          backgroundColor: "white",
          quality: 0.95,
          pixelRatio: 2,
        });

        // Create temporary link and trigger download
        const link = document.createElement("a");
        link.href = dataUrl;
        link.download = `${fileName}-content.jpg`;
        link.click();
      } catch (error) {
        console.error("Error generating JPEG image:", error);
      } finally {
        setIsDownloading(false);
      }
    }
  }, [fileName, contentRef]);

  const handleDownloadPDF = useCallback(async () => {
    setIsDropdownOpen(false);
    applyStylesToElements();
    await new Promise((resolve) => setTimeout(resolve, 100));
    if (contentRef.current) {
      try {
        setIsDownloading(true);

        // Apply text size enhancement
        const contentElement = contentRef.current;
        const originalFontSize =
          window.getComputedStyle(contentElement).fontSize;

        // Temporarily increase font size for better rendering
        contentElement.style.fontSize = "1.2em"; // Increase font size by 20%

        // Apply additional style adjustments for SVG elements
        const svgElements = contentElement.querySelectorAll("svg");
        svgElements.forEach((svg) => {
          // Apply scaling for svg text elements
          const textElements = svg.querySelectorAll("text");
          textElements.forEach((text) => {
            const computedStyle = window.getComputedStyle(text);
            const fontSize = parseFloat(computedStyle.fontSize);
            // Increase SVG text size by 20%
            text.setAttribute("font-size", `${fontSize * 1.2}px`);
          });
        });

        const canvas = await html2canvas(contentElement, {
          scale: 3, // Increase scale for better quality
          useCORS: true,
          logging: false,
          backgroundColor: "white",
          allowTaint: true,
          imageTimeout: 0, // No timeout
        });

        // Restore original font size
        contentElement.style.fontSize = originalFontSize;

        // Define margins for all sides
        const marginLeft = 10; // left margin in mm
        const marginTop = 10; // top margin in mm
        const marginBottom = 10; // bottom margin in mm

        // Calculate available print area
        const pageWidth = 210; // A4 width in mm
        const pageHeight = 297; // A4 height in mm

        const contentWidth = pageWidth - marginLeft * 2; // available width for content (left and right margins equal)
        const contentHeight = pageHeight - marginTop - marginBottom; // available height for content

        // Calculate image dimensions based on available print area
        const imgWidth = contentWidth;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;

        const pdf = new jsPDF({
          orientation: "portrait",
          unit: "mm",
          format: "a4",
        });

        // If the image is taller than one page, we need to split it
        const totalPages = Math.ceil(imgHeight / contentHeight);

        for (let pageNum = 0; pageNum < totalPages; pageNum++) {
          // After first page, add a new page
          if (pageNum > 0) {
            pdf.addPage();
          }

          // Calculate how much of the image to use for this page
          const yPosition = pageNum * contentHeight; // Position in the virtual complete image
          const currentPageHeight = Math.min(
            contentHeight,
            imgHeight - yPosition,
          );

          // Calculate scaling factor for this section
          const sectionHeight = (currentPageHeight / imgHeight) * canvas.height;
          const startY = (yPosition / imgHeight) * canvas.height;

          // Create a temporary canvas for this section
          const tempCanvas = document.createElement("canvas");
          const ctx = tempCanvas.getContext("2d");

          if (ctx) {
            tempCanvas.width = canvas.width;
            tempCanvas.height = sectionHeight;

            // Apply image smoothing for better quality
            ctx.imageSmoothingEnabled = true;
            ctx.imageSmoothingQuality = "high";

            // Draw only the section we need for this page
            ctx.drawImage(
              canvas,
              0,
              startY,
              canvas.width,
              sectionHeight,
              0,
              0,
              canvas.width,
              sectionHeight,
            );

            // Get image data for this section - use higher quality
            const pageImgData = tempCanvas.toDataURL("image/jpeg", 1.0);

            // Add image to the PDF
            pdf.addImage(
              pageImgData,
              "JPEG",
              marginLeft,
              marginTop,
              imgWidth,
              currentPageHeight,
            );
          }
        }

        pdf.save(`${fileName}.pdf`);
      } catch (error) {
        console.error("Error generating PDF:", error);
      } finally {
        setIsDownloading(false);
      }
    }
  }, [fileName, contentRef]);

  // Close dropdown when clicking outside
  useEffect(() => {
    // Add event listener to detect clicks outside the dropdown
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div ref={dropdownRef} className="relative">
      <button
        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
        disabled={isDownloading}
        className="flex items-center gap-1 px-2 py-1 rounded-full bg-gray-100/80 dark:bg-gray-700/80 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        aria-label="Download options"
        aria-expanded={isDropdownOpen}
        aria-haspopup="true"
      >
        {isDownloading ? (
          <svg
            className="animate-spin h-5 w-5 text-gray-600 dark:text-gray-300"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
            />
          </svg>
        ) : (
          <>
            <Download className="w-4 h-4 text-gray-600 dark:text-gray-300" />
            <span className="text-sm text-gray-600 dark:text-gray-300 hidden sm:inline">
              {t("common.download")}
            </span>
            <ChevronDown
              className={`w-3 h-3 text-gray-600 dark:text-gray-300 transition-transform duration-200 ${isDropdownOpen ? "rotate-180" : ""}`}
            />
          </>
        )}
      </button>

      {isDropdownOpen && (
        <div className="absolute right-0 mt-2 w-64 bg-white dark:bg-gray-800 shadow-lg rounded-md z-50 overflow-hidden border border-gray-200 dark:border-gray-700">
          <ul className="py-1">
            <li>
              <button
                onClick={handleDownloadPNGTransparent}
                className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center"
              >
                {t("common.png_transparent")}
              </button>
            </li>
            <li className="border-t border-gray-100 dark:border-gray-700">
              <div className="px-4 py-2 flex items-center justify-between">
                <button
                  onClick={handleDownloadPNGWithBackground}
                  className="text-left text-sm text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white flex-1"
                >
                  {t("common.png_with_background")}
                </button>
                <div className="flex items-center ml-2">
                  <label className="text-xs text-gray-500 dark:text-gray-400 mr-1">
                    {t("common.color")}
                  </label>
                  <input
                    type="color"
                    value={backgroundColor}
                    onChange={(e) => setBackgroundColor(e.target.value)}
                    className="w-6 h-6 rounded border border-gray-300 dark:border-gray-600 cursor-pointer"
                    aria-label="Choose background color"
                    title="Select background color"
                  />
                </div>
              </div>
            </li>
            <li className="border-t border-gray-100 dark:border-gray-700">
              <button
                onClick={handleDownloadJPEG}
                className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                {t("common.jpeg")}
              </button>
            </li>
            <li className="border-t border-gray-100 dark:border-gray-700">
              <button
                onClick={handleDownloadPDF}
                className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center"
              >
                <FileText className="w-4 h-4 mr-2" />
                {t("common.pdf")}
              </button>
            </li>
          </ul>
        </div>
      )}
    </div>
  );
}
